import { api, saveBlob } from './client';
import { endpoints } from './endpoints';

// -- profile -----------------------------------------------------------------
export function getProfile() {
  return api.get(endpoints.companies.me);
}

export function updateProfile(input) {
  return api.patch(endpoints.companies.me, input);
}

/**
 * Close the company account (owner only). A soft delete: support can restore it.
 * `{ password, company_name, reason? }`.
 */
export function closeCompany(input) {
  return api.post(endpoints.companies.close, input);
}

export function getCompletion() {
  return api.get(endpoints.companies.completion);
}

export function uploadLogo(file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.companies.logo, form);
}

// -- employer branding ------------------------------------------------------
// Every write answers with the whole branding view, completion included, so
// the editor replaces its cached copy with the response instead of refetching.
export function getBranding() {
  return api.get(endpoints.companies.branding);
}

// Omitted keys are left alone; null clears one.
export function updateBranding(input) {
  return api.patch(endpoints.companies.branding, input);
}

export function uploadCover(file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.companies.brandingCover, form);
}

export function removeCover() {
  return api.delete(endpoints.companies.brandingCover);
}

export function addGalleryPhoto(file, caption) {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);
  return api.upload(endpoints.companies.brandingGallery, form);
}

// Every photo id, exactly once, in the new order.
export function reorderGallery(mediaIds) {
  return api.put(endpoints.companies.brandingGalleryOrder, { media_ids: mediaIds });
}

export function updateGalleryPhoto(id, caption) {
  return api.patch(endpoints.companies.brandingGalleryById(id), { caption });
}

export function removeGalleryPhoto(id) {
  return api.delete(endpoints.companies.brandingGalleryById(id));
}

// -- verification ------------------------------------------------------------
export function getVerification() {
  return api.get(endpoints.companies.verification);
}

export function submitVerification(input) {
  return api.post(endpoints.companies.verification, input);
}

// -- members -----------------------------------------------------------------
export function listMembers(params) {
  return api.getPaginated(endpoints.companies.members, { params });
}

export function inviteMember(input) {
  return api.post(endpoints.companies.members, input);
}

export function updateMemberRole(id, input) {
  return api.patch(endpoints.companies.memberById(id), input);
}

export function removeMember(id) {
  return api.delete(endpoints.companies.memberById(id));
}

/** Where each of one member's permissions comes from: role, added, removed. */
export function getMemberPermissions(id) {
  return api.get(endpoints.companies.memberPermissions(id));
}

/** `permissions` is the complete set wanted; anything left out is switched off. */
export function updateMemberPermissions(id, permissions) {
  return api.put(endpoints.companies.memberPermissions(id), { permissions });
}

// -- roles and permissions ---------------------------------------------------
export function listRoles() {
  return api.get(endpoints.companies.roles);
}

export function updateRolePermissions(role, permissions) {
  return api.put(endpoints.companies.roleById(role), { permissions });
}

/** Back to the platform default for this role. */
export function resetRolePermissions(role) {
  return api.delete(endpoints.companies.roleById(role));
}

/** The signed-in member's own permissions in this workspace. */
export function getMyAccess() {
  return api.get(endpoints.companies.access);
}

// -- internships -------------------------------------------------------------
export function listInternships(params) {
  return api.getPaginated(endpoints.companies.internships, { params });
}

export function getInternshipCounts() {
  return api.get(endpoints.companies.internshipCounts);
}

export function getInternship(id) {
  return api.get(endpoints.companies.internshipById(id));
}

export function createInternship(input) {
  return api.post(endpoints.companies.internships, input);
}

export function updateInternship(id, input) {
  return api.patch(endpoints.companies.internshipById(id), input);
}

export function publishInternship(id) {
  return api.post(endpoints.companies.internshipPublish(id));
}

export function closeInternship(id) {
  return api.post(endpoints.companies.internshipClose(id));
}

export function deleteInternship(id) {
  return api.delete(endpoints.companies.internshipById(id));
}

// -- applications ------------------------------------------------------------
export function listApplications(params) {
  return api.getPaginated(endpoints.companies.applications, { params });
}

export function getApplicationCounts(params) {
  return api.get(endpoints.companies.applicationCounts, { params });
}

export function getApplication(id) {
  return api.get(endpoints.companies.applicationById(id));
}

export function changeApplicationStatus(id, input) {
  return api.patch(endpoints.companies.applicationStatus(id), input);
}

export function setApplicationNote(id, internal_note) {
  return api.patch(endpoints.companies.applicationNote(id), { internal_note });
}

// -- candidates --------------------------------------------------------------
export function searchCandidates(params) {
  return api.getPaginated(endpoints.companies.candidateSearch, { params });
}

export function listTalentPool(params) {
  return api.getPaginated(endpoints.companies.candidates, { params });
}

export function listCandidateTags() {
  return api.get(endpoints.companies.candidateTags);
}

export function getCandidate(id) {
  return api.get(endpoints.companies.candidateById(id));
}

export function addToTalentPool(input) {
  return api.post(endpoints.companies.candidates, input);
}

export function removeFromTalentPool(id) {
  return api.delete(endpoints.companies.candidateById(id));
}

export function setCandidateTags(id, tags) {
  return api.put(endpoints.companies.candidateTagsById(id), { tags });
}

export function listCandidateNotes(id) {
  return api.get(endpoints.companies.candidateNotes(id));
}

export function addCandidateNote(id, body) {
  return api.post(endpoints.companies.candidateNotes(id), { body });
}

export function deleteCandidateNote(id, noteId) {
  return api.delete(endpoints.companies.candidateNoteById(id, noteId));
}

// -- interviews --------------------------------------------------------------
export function listInterviews(params) {
  return api.getPaginated(endpoints.companies.interviews, { params });
}

/**
 * Every round overlapping `[start, end)`, unpaginated, for the calendar.
 * `start` and `end` are Dates at the viewer's local midnight; `toISOString`
 * carries the instant, so the API sees the same window the screen shows.
 */
export function getInterviewCalendar({ start, end, statuses }) {
  return api.get(endpoints.companies.interviewCalendar, {
    params: { start: start.toISOString(), end: end.toISOString(), statuses },
  });
}

export function getInterviewCounts() {
  return api.get(endpoints.companies.interviewCounts);
}

export function getInterview(id) {
  return api.get(endpoints.companies.interviewById(id));
}

export function scheduleInterview(input) {
  return api.post(endpoints.companies.interviews, input);
}

export function rescheduleInterview(id, input) {
  return api.patch(endpoints.companies.interviewById(id), input);
}

export function cancelInterview(id, reason) {
  return api.post(endpoints.companies.interviewCancel(id), { reason });
}

export function closeInterview(id, input) {
  return api.post(endpoints.companies.interviewClose(id), input);
}

export function recordInterviewFeedback(id, input) {
  return api.put(endpoints.companies.interviewFeedback(id), input);
}

/** Accept the candidate's request: `{ slot }` (one they proposed) or `{ scheduled_at, ... }`. */
export function acceptRescheduleRequest(id, input) {
  return api.post(endpoints.companies.interviewRescheduleAccept(id), input);
}

/** Decline the candidate's request. This cancels the interview. */
export function declineRescheduleRequest(id, reply) {
  return api.post(endpoints.companies.interviewRescheduleDecline(id), { reply });
}

// -- offers ------------------------------------------------------------------
export function listOffers(params) {
  return api.getPaginated(endpoints.companies.offers, { params });
}

export function getOfferCounts() {
  return api.get(endpoints.companies.offerCounts);
}

export function getOffer(id) {
  return api.get(endpoints.companies.offerById(id));
}

export function createOffer(input) {
  return api.post(endpoints.companies.offers, input);
}

export function updateOffer(id, input) {
  return api.put(endpoints.companies.offerById(id), input);
}

export function deleteOffer(id) {
  return api.delete(endpoints.companies.offerById(id));
}

export function sendOffer(id) {
  return api.post(endpoints.companies.offerSend(id));
}

export function withdrawOffer(id, reason) {
  return api.post(endpoints.companies.offerWithdraw(id), { reason });
}

/** The letter the candidate gets -- uploaded or generated -- saved as a PDF. */
export async function downloadOfferLetter(id) {
  const { blob, filename } = await api.download(`${endpoints.companies.offerById(id)}/letter`);
  saveBlob(blob, filename.endsWith('.pdf') ? filename : 'offer-letter.pdf');
}

/** Replace the generated letter with the company's own signed PDF. */
export function uploadOfferLetter(id, file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(`${endpoints.companies.offerById(id)}/letter`, form);
}

/** Drop the uploaded letter; the candidate gets the generated one again. */
export function removeOfferLetter(id) {
  return api.delete(`${endpoints.companies.offerById(id)}/letter`);
}

// -- public ------------------------------------------------------------------
export function getPublicCompany(slug) {
  return api.get(endpoints.companies.bySlug(slug));
}

// -- billing -----------------------------------------------------------------
// `changePlan` is the only call here that costs money. The response says
// whether it took effect now or waits for the period already paid for, and
// the screen repeats that back rather than assuming either.
export function getSubscription() {
  return api.get(endpoints.companies.subscription);
}

export function listSubscriptionPlans() {
  return api.get(endpoints.companies.subscriptionPlans);
}

export function changePlan(planCode, couponCode) {
  return api.post(endpoints.companies.subscription, {
    plan_code: planCode,
    ...(couponCode ? { coupon_code: couponCode } : {}),
  });
}

// Prices a code against a plan and spends nothing. The code is only redeemed
// by the plan change itself, so a customer can check before committing.
export function previewCoupon(code, planCode) {
  return api.post(endpoints.companies.subscriptionCouponPreview, {
    code,
    plan_code: planCode,
  });
}

export function resumeSubscription() {
  return api.post(endpoints.companies.subscriptionResume);
}

export function listInvoices() {
  return api.get(endpoints.companies.invoices);
}

/** Saves one invoice as a PDF. */
export async function downloadInvoicePdf(invoice) {
  const { blob, filename } = await api.download(
    `${endpoints.companies.invoiceById(invoice.id)}/pdf`,
  );
  saveBlob(blob, filename.endsWith('.pdf') ? filename : `${invoice.number}.pdf`);
}

// -- ATS integration ---------------------------------------------------------
// `getIntegration` answers even when nothing is connected: the connector
// catalogue and whether the plan covers automation come back with it, so the
// screen never has to ask three times to decide what to draw.
export function getIntegration() {
  return api.get(endpoints.companies.ats);
}

export function saveIntegration(input) {
  return api.put(endpoints.companies.ats, input);
}

export function disconnectIntegration() {
  return api.delete(endpoints.companies.ats);
}

export function testIntegration() {
  return api.post(endpoints.companies.atsTest);
}

export function listPendingHandover() {
  return api.get(endpoints.companies.atsPending);
}

// An empty list is the ordinary case and means everyone who qualifies.
export function exportCandidates(applicationIds = []) {
  return api.post(endpoints.companies.atsExport, { application_ids: applicationIds });
}

export function listHandovers(params) {
  return api.getPaginated(endpoints.companies.atsHandovers, { params });
}

export function getHandoverDownload(id) {
  return api.get(endpoints.companies.atsHandoverDownload(id));
}
